import { DomainError, TransportError, } from "@econ-v1/domain";
import { isJsonValue } from "@econ-v1/rpc";
import { createWebSocketEventQueue, parseWebSocketEventMessage, } from "./websocket-events.js";
export class HttpWsHomeNodeClient {
    #accessToken;
    #baseUrl;
    #delegationCredential;
    #eventQueue = createWebSocketEventQueue();
    #fetch;
    #listeners = new Set();
    #readinessWaiters = new Set();
    #now;
    #reconnectDelayMs;
    #scheduleReconnect;
    #syncDomains;
    #transportPath;
    #websocketFactory;
    #closed = false;
    #identifiedGeneration;
    #reconnectAttempt = 0;
    #reconnectTask;
    #websocketGeneration = 0;
    #websocket;
    state;
    constructor(options) {
        this.#accessToken = options.accessToken ?? (async () => undefined);
        this.#baseUrl = new URL(options.baseUrl);
        this.#delegationCredential = options.delegationCredential ?? (async () => undefined);
        this.#fetch = options.fetch ?? globalThis.fetch.bind(globalThis);
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#reconnectDelayMs = options.reconnectDelayMs ?? ((attempt) => Math.min(1000 * 2 ** Math.max(0, attempt - 1), 30_000));
        this.#scheduleReconnect = options.scheduleReconnect ?? defaultReconnectScheduler;
        this.#syncDomains = new Set(options.syncDomains ?? []);
        this.#transportPath = options.transportPath ?? "wan-direct";
        this.#websocketFactory = options.websocketFactory ?? defaultWebSocketFactory;
        this.state = {
            changedAt: this.#now(),
            path: "offline",
            status: "offline",
        };
        this.#websocket = this.#connectWebSocket();
    }
    close() {
        if (this.#closed)
            return;
        this.#closed = true;
        this.#rejectReadiness(new TransportError({ code: "offline", message: "Home node connection closed before readiness" }));
        this.#reconnectTask?.cancel();
        this.#reconnectTask = undefined;
        this.#websocket?.close();
        this.#eventQueue.close();
        this.#listeners.clear();
    }
    ready(signal) {
        if (this.state.status === "connected")
            return Promise.resolve();
        if (this.#closed) {
            return Promise.reject(new TransportError({ code: "offline", message: "Home node connection is closed" }));
        }
        return new Promise((resolve, reject) => {
            const waiter = {
                reject: (error) => {
                    signal?.removeEventListener("abort", abort);
                    reject(error);
                },
                resolve: () => {
                    signal?.removeEventListener("abort", abort);
                    resolve();
                },
            };
            const abort = () => {
                this.#readinessWaiters.delete(waiter);
                waiter.reject(new TransportError({ code: "offline", message: "Home node readiness was aborted" }));
            };
            if (signal?.aborted === true) {
                abort();
                return;
            }
            signal?.addEventListener("abort", abort, { once: true });
            this.#readinessWaiters.add(waiter);
        });
    }
    deltas(request) {
        if (this.#syncDomains.has(request.domain))
            return this.#fetchDeltaPages(request);
        return this.#eventQueue.stream({ domain: request.domain });
    }
    async invoke(request) {
        const parsed = parseOperation(request.operation);
        let response;
        try {
            const headers = new Headers();
            const accessToken = await this.#accessToken();
            if (accessToken !== undefined)
                headers.set("authorization", `Bearer ${accessToken}`);
            const delegation = await this.#delegationCredential();
            if (delegation !== undefined)
                headers.set("x-client-device-delegation", delegation);
            for (const [name, value] of Object.entries(request.headers ?? {}))
                headers.set(name, value);
            const init = { headers, method: parsed.method };
            if (parsed.method === "POST") {
                init.body = JSON.stringify(requestBody(parsed.path, request.payload));
                headers.set("content-type", "application/json");
            }
            response = await this.#fetch(new Request(new URL(parsed.path, this.#baseUrl), init));
        }
        catch {
            throw new TransportError({ code: "offline", message: "Home node request failed" });
        }
        const envelope = await readEnvelope(response);
        if (envelope.success === false)
            throw mapEnvelopeError(envelope.error);
        if (envelope.success !== true)
            throw new TransportError({ code: "unavailable", message: "Home node returned an invalid response" });
        if (parsed.path === "/api/v2/system/capabilities/invoke") {
            if (!isRecord(envelope.data) || !isJsonValue(envelope.data.result)) {
                throw new TransportError({ code: "unavailable", message: "Capability response was malformed" });
            }
            return envelope.data.result;
        }
        if (!isJsonValue(envelope.data)) {
            throw new TransportError({ code: "unavailable", message: "Home node returned non-JSON data" });
        }
        return envelope.data;
    }
    subscribe(listener) {
        this.#listeners.add(listener);
        return () => {
            this.#listeners.delete(listener);
        };
    }
    #publishDelta(event) {
        this.#eventQueue.publish(event);
        for (const listener of [...this.#listeners])
            listener({ data: event.data, domain: event.type, type: "delta" });
    }
    async *#fetchDeltaPages(request) {
        let cursor = request.cursor;
        for (;;) {
            const page = await this.#fetchDeltaPage(request.domain, cursor);
            for (const delta of page.deltas)
                yield delta;
            if (!page.hasMore)
                return;
            if (page.nextCursor === undefined || page.nextCursor === cursor) {
                throw new TransportError({ code: "unavailable", message: "Home node returned an invalid delta page cursor" });
            }
            cursor = page.nextCursor;
        }
    }
    async #fetchDeltaPage(domain, cursor) {
        let response;
        try {
            const headers = await this.#authorizationHeaders();
            const url = new URL(`/api/v2/client-sync/${encodeURIComponent(domain)}/deltas`, this.#baseUrl);
            if (cursor !== undefined)
                url.searchParams.set("since", cursor);
            response = await this.#fetch(new Request(url, { headers, method: "GET" }));
        }
        catch {
            throw new TransportError({ code: "offline", message: "Home node delta request failed" });
        }
        const envelope = await readEnvelope(response);
        if (envelope.success === false)
            throw mapEnvelopeError(envelope.error);
        if (envelope.success !== true) {
            throw new TransportError({ code: "unavailable", message: "Home node returned an invalid delta response" });
        }
        return parseDeltaPage(envelope.data);
    }
    async #authorizationHeaders() {
        const headers = new Headers();
        const accessToken = await this.#accessToken();
        if (accessToken !== undefined)
            headers.set("authorization", `Bearer ${accessToken}`);
        const delegation = await this.#delegationCredential();
        if (delegation !== undefined)
            headers.set("x-client-device-delegation", delegation);
        return headers;
    }
    #setState(next) {
        const unchanged = this.state.status === next.status &&
            this.state.path === next.path;
        if (unchanged)
            return;
        this.state = next;
        for (const listener of [...this.#listeners])
            listener({ state: next, type: "transport.state-changed" });
    }
    #connectWebSocket() {
        const generation = ++this.#websocketGeneration;
        let socket;
        try {
            socket = this.#websocketFactory(this.#websocketUrl());
        }
        catch {
            this.#handleDisconnect();
            return undefined;
        }
        socket.addEventListener("open", () => {
            if (generation !== this.#websocketGeneration || this.#closed)
                return;
            void this.#finishReadiness(socket, generation);
        });
        socket.addEventListener("close", () => {
            if (generation !== this.#websocketGeneration)
                return;
            this.#handleDisconnect();
        });
        socket.addEventListener("error", () => {
            if (generation !== this.#websocketGeneration)
                return;
            this.#handleDisconnect();
        });
        socket.addEventListener("message", (event) => {
            if (generation !== this.#websocketGeneration || this.#closed)
                return;
            const payload = messageData(event);
            if (payload === undefined)
                return;
            const acknowledgement = parseConnectionEstablished(payload);
            if (acknowledgement !== undefined) {
                if (acknowledgement !== false)
                    this.#acknowledgeReadiness(generation);
                return;
            }
            const parsed = parseWebSocketEventMessage(payload);
            if (parsed === undefined)
                return;
            this.#publishDelta(parsed);
        });
        return socket;
    }
    async #identifyWebSocket(socket, generation) {
        const [sessionToken, delegationCredential] = await Promise.all([
            this.#accessToken(),
            this.#delegationCredential(),
        ]);
        if (generation !== this.#websocketGeneration || this.#closed) {
            throw new TransportError({ code: "offline", message: "Home node connection changed before readiness" });
        }
        this.#identifiedGeneration = generation;
        socket.send(JSON.stringify({
            action: "identify",
            ...(delegationCredential === undefined ? {} : { delegation_credential: delegationCredential }),
            ...(sessionToken === undefined ? {} : { session_token: sessionToken }),
        }));
    }
    async #finishReadiness(socket, generation) {
        try {
            await this.#identifyWebSocket(socket, generation);
        }
        catch {
            if (this.#identifiedGeneration === generation)
                this.#identifiedGeneration = undefined;
            if (generation === this.#websocketGeneration)
                this.#handleDisconnect();
            return;
        }
    }
    #acknowledgeReadiness(generation) {
        if (generation !== this.#websocketGeneration ||
            generation !== this.#identifiedGeneration ||
            this.#closed) {
            return;
        }
        this.#identifiedGeneration = undefined;
        this.#reconnectAttempt = 0;
        this.#reconnectTask?.cancel();
        this.#reconnectTask = undefined;
        this.#setState({
            changedAt: this.#now(),
            path: this.#transportPath,
            status: "connected",
        });
        for (const waiter of [...this.#readinessWaiters]) {
            this.#readinessWaiters.delete(waiter);
            waiter.resolve();
        }
    }
    #handleDisconnect() {
        if (this.#closed)
            return;
        this.#identifiedGeneration = undefined;
        this.#rejectReadiness(new TransportError({ code: "offline", message: "Home node connection failed before readiness" }));
        this.#setState({ changedAt: this.#now(), path: "offline", status: "offline" });
        if (this.#reconnectTask !== undefined)
            return;
        this.#reconnectAttempt += 1;
        this.#reconnectTask = this.#scheduleReconnect(this.#reconnectDelayMs(this.#reconnectAttempt), () => {
            this.#reconnectTask = undefined;
            if (this.#closed)
                return;
            this.#websocket = this.#connectWebSocket();
        });
    }
    #rejectReadiness(error) {
        for (const waiter of [...this.#readinessWaiters]) {
            this.#readinessWaiters.delete(waiter);
            waiter.reject(error);
        }
    }
    #websocketUrl() {
        const protocol = this.#baseUrl.protocol === "https:" ? "wss:" : "ws:";
        const url = new URL("/api/ws", this.#baseUrl);
        url.protocol = protocol;
        return url.toString();
    }
}
function defaultWebSocketFactory(url) {
    return new WebSocket(url);
}
function defaultReconnectScheduler(delayMs, callback) {
    const handle = globalThis.setTimeout(callback, delayMs);
    return {
        cancel() {
            globalThis.clearTimeout(handle);
        },
    };
}
function mapEnvelopeError(error) {
    const code = error?.code;
    const message = error?.message ?? "Home node request failed";
    switch (code) {
        case "BAD_REQUEST":
        case "VALIDATION_ERROR":
            return new DomainError({ code: "invalid_request", message });
        case "UNAUTHORIZED":
        case "FORBIDDEN":
            return new DomainError({ code: "unauthorized", message });
        case "NOT_FOUND":
            return new DomainError({ code: "not_found", message });
        case "CONFLICT":
            return new DomainError({ code: "conflict", message });
        default:
            return new TransportError({ code: "unavailable", message });
    }
}
function messageData(event) {
    if (!isRecord(event) || typeof event.data !== "string")
        return undefined;
    return event.data;
}
function parseConnectionEstablished(serialized) {
    let value;
    try {
        value = JSON.parse(serialized);
    }
    catch {
        return undefined;
    }
    if (!isRecord(value) || value.type !== "connection_established")
        return undefined;
    if (!isExactRecord(value, ["data", "type"]) || !isExactRecord(value.data, ["connection_id", "user_id"])) {
        return false;
    }
    return nonEmptyString(value.data.connection_id) && nonEmptyString(value.data.user_id);
}
function parseOperation(operation) {
    const [method, path] = operation.split(" ", 2);
    if ((method !== "GET" && method !== "POST") || typeof path !== "string" || path.length === 0) {
        throw new DomainError({ code: "invalid_request", message: `Unsupported home node operation: ${operation}` });
    }
    return { method, path };
}
function requestBody(path, payload) {
    if (!isRecord(payload))
        return {};
    if (path !== "/api/v2/system/capabilities/invoke")
        return payload;
    return {
        capability: stringField(payload, "capability"),
        payload: jsonField(payload, "payload"),
    };
}
async function readEnvelope(response) {
    let value;
    try {
        value = await response.json();
    }
    catch {
        throw new TransportError({ code: "unavailable", message: "Home node returned invalid JSON" });
    }
    if (!isRecord(value))
        throw new TransportError({ code: "unavailable", message: "Home node returned an invalid response" });
    return value;
}
function parseDeltaPage(value) {
    // The real server response (`core/domain/src/models/client_sync.rs`
    // `ClientSyncPage`, exercised end-to-end by #1887's real-harness gate) names
    // the delta array `changes`, not `deltas` — `deltas` is only this client's
    // own internal `DeltaPage.deltas` field name. Reading the wrong wire field
    // silently classified every real delta page as malformed
    // (`TransportError({code: "unavailable"})`), which `ManagedHomeNodeClient`
    // treats as a path failure and retires the whole shared transport client on
    // every sync tick — the real root cause the connectivity badge never
    // reaching "Connected" in a real end-to-end run (`docs/development/
    // client-node-real-harness-gate.md`, Round 4).
    if (!isRecord(value) || !Array.isArray(value.changes) || typeof value.has_more !== "boolean") {
        throw new TransportError({ code: "unavailable", message: "Home node returned a malformed delta page" });
    }
    if (!value.changes.every((delta) => isJsonValue(delta))) {
        throw new TransportError({ code: "unavailable", message: "Home node returned a non-JSON delta" });
    }
    if (value.next_cursor !== undefined && value.next_cursor !== null && typeof value.next_cursor !== "string") {
        throw new TransportError({ code: "unavailable", message: "Home node returned an invalid delta cursor" });
    }
    return {
        deltas: value.changes,
        hasMore: value.has_more,
        ...(typeof value.next_cursor === "string" ? { nextCursor: value.next_cursor } : {}),
    };
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isExactRecord(value, keys) {
    if (!isRecord(value))
        return false;
    const actual = Object.keys(value).sort();
    const expected = [...keys].sort();
    return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}
function nonEmptyString(value) {
    return typeof value === "string" && value.trim() === value && value.length > 0;
}
function jsonField(value, key) {
    const field = value[key];
    if (!isJsonValue(field))
        throw new DomainError({ code: "invalid_request", message: `Expected ${key} to be JSON` });
    return field;
}
function stringField(value, key) {
    const field = value[key];
    if (typeof field !== "string" || field.length === 0) {
        throw new DomainError({ code: "invalid_request", message: `Expected ${key} to be a non-empty string` });
    }
    return field;
}
//# sourceMappingURL=http-home-node-client.js.map